import * as L from 'leaflet';
import { maplibreGL } from '@maplibre/maplibre-gl-leaflet';
import { setWorkerUrl, type StyleSpecification } from 'maplibre-gl';
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import 'maplibre-gl/dist/maplibre-gl.css';
import 'leaflet/dist/leaflet.css';
import { styleOrderBasemap } from './osm-style';

setWorkerUrl(workerUrl);

/** Tashkent `[lat, lng]` — the view before the first fit. */
export const TASHKENT_LATLNG: L.LatLngTuple = [41.3112, 69.2797];

/** Leaflet wants `[lat, lng]`; the app carries `[lng, lat]`. */
export const toLatLng = ([lng, lat]: [number, number]): L.LatLngTuple => [lat, lng];

/** Add the shared order-detail basemap to an existing Leaflet map. */
export function addOrderBasemap(
  map: L.Map,
  onTileState?: (failed: boolean) => void,
): () => void {
  const controller = new AbortController();
  let fallback: L.TileLayer | undefined;

  void fetch('https://tiles.openfreemap.org/styles/liberty', {
    signal: controller.signal,
  })
    .then(async (response) => {
      if (!response.ok) throw new Error('Basemap style unavailable');
      const style = (await response.json()) as StyleSpecification;
      for (const source of Object.values(style.sources)) {
        if ('attribution' in source && typeof source.attribution === 'string') {
          source.attribution = source.attribution
            .replace(/<a\b[^>]*>\s*OpenFreeMap\s*<\/a>/gi, '')
            .replace(/OpenFreeMap/gi, '')
            .trim();
        }
      }
      if (!controller.signal.aborted)
        maplibreGL({ style: styleOrderBasemap(style) }).addTo(map);
    })
    .catch(() => {
      if (controller.signal.aborted) return;
      fallback = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(map);
      fallback.on('tileerror', () => onTileState?.(true));
      fallback.on('tileload', () => onTileState?.(false));
    });

  return () => {
    controller.abort();
    fallback?.remove();
  };
}

/**
 * A Leaflet map on the quiet OpenFreeMap vector basemap (restyled by
 * {@link styleOrderBasemap}), degrading to raw OSM raster tiles when the style
 * cannot be fetched. Shared by every OSM-backed order map so they look and fail
 * the same way. `dispose` tears the whole thing down (idempotent).
 */
export function createOrderMap(container: HTMLElement): {
  map: L.Map;
  dispose: () => void;
} {
  const map = L.map(container, { zoomControl: false, attributionControl: false }).setView(
    TASHKENT_LATLNG,
    13,
  );

  const disposeBasemap = addOrderBasemap(map);

  const observer = new ResizeObserver(() => map.invalidateSize());
  observer.observe(container);

  return {
    map,
    dispose: () => {
      observer.disconnect();
      disposeBasemap();
      map.remove();
    },
  };
}
